<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="billetage-form container p-15 p-md-40">
      <h5 class="fw-bold text-black mb-15 mb-md-25">{{ title }}</h5>
      <div v-if="!caisseActive" class="alert alert-warning">
        Aucune caisse active n'est disponible. Veuillez ouvrir une caisse pour effectuer un paiement.
        <button v-if="showCaisseClosedMessage" class="btn btn-primary btn-sm mt-2" @click="ouvrirCaisse()">
          Ouvrir la caisse
        </button>
      </div>

      <Form ref="billetageForm" @submit="addPaiement" :validation-schema="paiementSchema"
        :class="{ 'disabled-form': !caisseActive }">
        <div class="div row">
          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de paiement
              </label>
              <Field name="datePaiement" type="date" :value="getCurrentDate()"
                class="form-control shadow-none fs-md-15 text-black" :disabled="!caisseActive" />
              <ErrorMessage name="datePaiement" class="text-danger" />
            </div>
          </div>

          <div class="div col-md-12">
            <div class="div row">
              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Commandes non payées <span class="text-danger">*</span>
                  </label>
                  <Field name="commande" v-slot="{ field }">
                    <Multiselect :options="commandesOptions" :searchable="true" track-by="value" label="label"
                      v-model="field.value" v-bind="field" placeholder="Sélectionner la commande"
                      @change="handleCommandeChange" :disabled="!caisseActive" />
                  </Field>
                </div>
                <ErrorMessage name="commande" class="text-danger" />
              </div>
              <div v-if="selectedCommande" class="alert alert-info mt-2">
                <strong>Récapitulatif :</strong>
                <ul class="mb-0 mt-2">
                  <li>Montant total de la commande : {{ commandeSelectionnee.montTTC }} Fcfa</li>
                  <li>Montant déjà payé : {{ commandeSelectionnee.montantDejaPaye }} Fcfa</li>
                  <li>Montant restant à payer : {{ commandeSelectionnee.montantRest }} Fcfa</li>
                  <li v-if="commandeSelectionnee.statutPaiement === 'Partielle'" class="text-warning">
                    Cette commande a un paiement partiel en cours
                  </li>
                </ul>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant TTC <span class="text-danger">*</span>
                  </label>
                  <Field name="montantTtc" v-model="montantTtc">
                    <input type="number" v-model="montantTtc" class="form-control shadow-none fs-md-15 text-black"
                      placeholder="Entrer le montant" readonly :disabled="!caisseActive" />
                  </Field>
                  <ErrorMessage name="montantTtc" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="div row">
              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Mode de paiement <span class="text-danger">*</span>
                  </label>
                  <Field name="modePaiement" v-slot="{ field }" :value="1">
                    <Multiselect :options="modePaiementOptions" :searchable="true" track-by="value" label="label"
                      v-model="field.value" v-bind="field" placeholder="Sélectionner le mode paiement"
                      @change="handleModePaiementChange" :disabled="!caisseActive" />
                  </Field>
                </div>
                <ErrorMessage name="modePaiement" class="text-danger" />
              </div>

              <div v-if="selectedModePaiement === 2" class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Numéro de transaction Mobile Money <span class="text-danger">*</span>
                  </label>
                  <Field name="numeroMobileMoney" v-model="numeroMobileMoney" v-slot="{ field }">
                    <vue-tel-input placeholder="Entrer le numéro de téléphone" v-model="field.value" v-bind="field"
                      defaultCountry="BJ" mode="international" @validate="validate"
                      class="shadow-none fs-md-15 text-black py-2" :disabled="!caisseActive" />
                  </Field>
                  <div v-if="numeroMobileMoney && !validPhone" class="text-danger">Veuillez entrer un numéro correct
                  </div>
                  <ErrorMessage name="numeroMobileMoney" class="text-danger" />
                </div>
              </div>

              <div v-if="selectedModePaiement === 2" class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Référence <span class="text-danger">*</span>
                  </label>
                  <Field name="refOperation" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer la référence du compte" :disabled="!caisseActive" />
                  <ErrorMessage name="refOperation" class="text-danger" />
                </div>
              </div>

              <div v-if="selectedModePaiement === 4" class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Numéro Virement <span class="text-danger">*</span>
                  </label>
                  <Field name="numeroVirement" type="number" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer le numéro de virement" :disabled="!caisseActive" />
                  <ErrorMessage name="numeroVirement" class="text-danger" />
                </div>
              </div>

              <div v-if="selectedModePaiement === 3" class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Numéro de chèque <span class="text-danger">*</span>
                  </label>
                  <Field name="numCheque" type="number" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer le numéro de chèque" :disabled="!caisseActive" />
                  <ErrorMessage name="numCheque" class="text-danger" />
                </div>
              </div>

              <div v-if="selectedModePaiement === 3 || selectedModePaiement === 4" class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Banque <span class="text-danger">*</span>
                  </label>
                  <Field name="banque" v-slot="{ field }">
                    <Multiselect :options="banqueOptions" :searchable="true" track-by="value" label="label"
                      v-model="field.value" v-bind="field" placeholder="Sélectionner la banque"
                      :disabled="!caisseActive" />
                  </Field>
                  <ErrorMessage name="banque" class="text-danger" />
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant Payé <span class="text-danger">*</span>
                  </label>
                  <Field name="montantPaye" v-slot="{ field }">
                    <input type="number" v-model.number="montantPaye" v-bind="field"
                      class="form-control shadow-none fs-md-15 text-black" placeholder="Entrer le montant payé"
                      :readonly="selectedModePaiement === 1" @input="field.onInput($event)" :disabled="!caisseActive" />
                    <ErrorMessage name="montantPaye" class="text-danger" />
                  </Field>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant Reste à Payé <span class="text-danger">*</span>
                  </label>
                  <Field name="montantRest" v-slot="{ field }">
                    <input type="number" v-bind="field" class="form-control shadow-none fs-md-15 text-black"
                      placeholder="Entrer le montant reste à payé" readonly :disabled="!caisseActive" />
                    <ErrorMessage name="montantRest" class="text-danger" />
                  </Field>
                </div>
              </div>

              <div v-if="selectedModePaiement === 1 && caisseActive" class="row">
                <!-- Billetage Encaissement -->
                <div class="div col-md-6 mb-md-12">
                  <div class="div border border-primary mb-5">
                    <div class="div row fw-bold py-2" style="background-color: #0a59a4">
                      <div class="div col-md-7">
                        <h3 class="h3 fs-4 text-white">Billetage Paiement</h3>
                      </div>
                      <div class="col-md-5">
                        <h3 class="fs-5 text-white" style="text-align: end;font-weight: bold;"
                          v-if="montantTotalEncaissement">{{ montantTotalEncaissement }} Fcfa</h3>
                      </div>
                    </div>
                    <div class="div px-3">
                      <div class="div row mt-2">
                        <div class="div col-md-4"><label class="label fw-semibold">Monnaie</label></div>
                        <div class="div col-md-4"><label class="label fw-semibold">Quantité</label></div>
                        <div class="div col-md-4"><label class="label fw-semibold">Montant</label></div>
                      </div>
                      <hr class="hr mt-0" />
                      <template v-for="(billetage, index) in billetageList" :key="index">
                        <div class="div container-tight m-0 py-1">
                          <div class="div row align-items-center">
                            <div class="div col-md-4 px-1">
                              <input name="monnaie" v-model="billetage.libelle" readonly
                                class="form-control compact-input" placeholder="Monnaie" />
                            </div>
                            <div class="div col-md-4 px-1">
                              <Field :name="`billetageList[${index}].qteBillet`" type="number" min="0"
                                v-model="billetage.qteBillet"
                                @input="event => handleBilletageInput(event, billetage, 'encaissement')"
                                @keypress="restrictInput($event)" class="form-control compact-input" placeholder="Qté"
                                :disabled="!caisseActive" />
                              <ErrorMessage :name="`billetageList[${index}].qteBillet`"
                                class="text-danger compact-error" />
                            </div>
                            <div class="div col-md-4 px-1">
                              <input type="text" v-model="billetage.montant" readonly placeholder="Montant"
                                class="form-control compact-input" />
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <div class="div col-md-6 mb-md-12">
                  <div class="div border mb-5"
                    :class="montantManquant > 0 ? 'border-indigo-500 bg-indigo-50' : 'border-danger'">
                    <div class="div row fw-bold py-2"
                      :style="montantManquant > 0 ? 'background-color: #6366f1; color: white' : 'background-color: #a40a0a'">
                      <div class="div col-md-7">
                        <h3 class="h3 fs-4 text-white">
                          {{ montantManquant > 0 ? 'Montant Manquant' : 'Reliquat à Rendre' }}
                        </h3>
                      </div>
                      <div class="col-md-5">
                        <h3 class="fs-5 text-white" style="text-align: end;font-weight: bold;">
                          {{ montantManquant > 0 ? montantManquant : reliquatCalcule }} Fcfa
                        </h3>
                      </div>
                    </div>

                    <div class="div px-3" v-if="montantManquant === 0">
                      <!-- Billetage sortie normal -->
                      <div class="div row mt-2">
                        <div class="div col-md-4"><label class="label fw-semibold">Monnaie</label></div>
                        <div class="div col-md-4"><label class="label fw-semibold">Quantité</label></div>
                        <div class="div col-md-4"><label class="label fw-semibold">Montant</label></div>
                      </div>
                      <hr class="hr mt-0" />
                      <template v-for="(billetage, index) in billetageSortieList" :key="index">
                        <div class="div container-tight m-0 py-1">
                          <div class="div row align-items-center">
                            <div class="div col-md-4 px-1">
                              <input name="monnaie" v-model="billetage.libelle" readonly
                                class="form-control compact-input" placeholder="Monnaie" />
                            </div>
                            <div class="div col-md-4 px-1">
                              <Field :name="`billetageList[${index}].qteBillet`" type="number" min="0"
                                v-model="billetage.qteBillet"
                                @input="event => handleBilletageInput(event, billetage, 'encaissement')"
                                @blur="() => { if (!billetage.qteBillet) billetage.qteBillet = 0 }"
                                @keypress="restrictInput($event)" class="form-control compact-input" placeholder="Qté"
                                :disabled="!caisseActive" />
                              <ErrorMessage :name="`billetageSortieList[${index}].qteBillet`"
                                class="text-danger compact-error" />
                            </div>
                            <div class="div col-md-4 px-1">
                              <input type="text" v-model="billetage.montant" readonly placeholder="Montant"
                                class="form-control compact-input" />
                            </div>
                          </div>
                        </div>
                      </template>
                      <div v-if="montantTotalSortie > reliquatCalcule" class="text-danger mt-2">
                        Le billetage de sortie ({{ montantTotalSortie }} Fcfa) dépasse le reliquat ({{ reliquatCalcule
                        }} Fcfa).
                      </div>
                    </div>

                    <div class="div px-3 py-4 text-center" v-else>
                      <i class="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                      <p class="mb-2 text-dark fw-bold">
                        Paiement Partiel
                      </p>
                      <p class="mb-0 text-muted">
                        Il manque encore <strong class="text-warning">{{ montantManquant }} Fcfa</strong>
                      </p>
                      <p class="mb-0 text-muted small mt-2">
                        Le client pourra compléter le paiement plus tard
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedModePaiement === 1" class="row mt-3">
              <div class="col-md-10"></div>
              <div class="col-md-2">
                <div class="form-group mb-2">
                  <label class="form-label fw-semibold mb-1 text-start d-block">
                    Reliquat à retourner
                  </label>
                  <input type="number" class="form-control form-control-sm shadow-none text-start"
                    v-model.number="reliquatRetourner" readonly :class="{ 'is-warning': reliquatRetourner > 0 }" />
                  <div v-if="reliquatRetourner > 0" class="text-black mt-2">
                    Reliquat non rendu : {{ reliquatRetourner }} Fcfa.
                  </div>
                </div>
              </div>
            </div>

            <div class="div mb-3 mt-1">
              <button type="submit" class="btn btn-primary top-end"
                :disabled="montantError || !caisseActive">Envoyer</button>
              <button type="button" class="btn btn-danger mx-1" @click="cancelOperation">Annuler</button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, watch, onMounted, computed } from 'vue';
import { Form, Field, ErrorMessage, useForm } from 'vee-validate';
import * as Yup from 'yup';
import Multiselect from '@vueform/multiselect';
import { useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error, format_date, monSwal, success } from '@/utils/utils';
import { countries } from '@/utils/countries';
import 'vue3-tel-input/dist/vue3-tel-input.css';
import { VueTelInput } from 'vue3-tel-input';
import Swal from 'sweetalert2';
import { useAuthStore } from '@/services/auth';

interface BilletageForm {
  libelle: string;
  qteBillet: number;
  montant: number;
  monnaie: number;
  valueAct: number;
}

export default defineComponent({
  name: "AddPaiement",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
    VueTelInput
  },
  props: {
    title: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const paiementSchema = Yup.object({
      datePaiement: Yup.date().required('La date de paiement est obligatoire').nullable(),
      commande: Yup.number().required('La commande est obligatoire').nullable(),
      modePaiement: Yup.number().required('Le mode de paiement est obligatoire').nullable(),
      banque: Yup.string().when('modePaiement', {
        is: (value: number) => value === 3 || value === 4,
        then: (schema) => schema.required('La banque est obligatoire'),
        otherwise: (schema) => schema.notRequired()
      }),
      numeroMobileMoney: Yup.string().when('modePaiement', {
        is: 2,
        then: (schema) => schema.required('Le numéro Mobile Money est obligatoire'),
        otherwise: (schema) => schema.notRequired()
      }),
      refOperation: Yup.string().when('modePaiement', {
        is: 2,
        then: (schema) => schema.required('La référence du compte est obligatoire'),
        otherwise: (schema) => schema.notRequired()
      }),
      numeroVirement: Yup.number().when('modePaiement', {
        is: 4,
        then: (schema) => schema.required('Le numéro de virement est obligatoire'),
        otherwise: (schema) => schema.notRequired()
      }),
      numCheque: Yup.number().when('modePaiement', {
        is: 3,
        then: (schema) => schema.required('Le numéro de chèque est obligatoire'),
        otherwise: (schema) => schema.notRequired()
      }),
      montantTtc: Yup.number()
        .typeError('Le montant doit être un nombre valide')
        .required('Le montant est obligatoire')
        .min(0, 'Le montant doit être positif'),
      montantPaye: Yup.number()
        .typeError('Le montant payé doit être un nombre valide')
        .required('Le montant payé est obligatoire')
        .min(0, 'Le montant payé doit être positif')
        .when('modePaiement', {
          is: 1,
          then: (schema) =>
            schema.test(
              'match-montant-ttc',
              'Le montant payé doit être égal au montant encaissé',
              function (value) {
                return value === montantTotalEncaissement.value;
              }
            ),
          otherwise: (schema) => schema,
        }),
      montantRest: Yup.number()
        .typeError('Le montant restant doit être un nombre valide')
        .required('Le montant restant est obligatoire')
        .min(0, 'Le montant restant doit être positif')
        .test(
          'montant-rest-validation',
          'Le montant restant doit être cohérent',
          function (value) {
            const montantTtcValue = montantTtc.value || 0;

            if (selectedModePaiement.value === 1) {
              const encaissement = montantTotalEncaissement.value || 0;
              const expected = montantTtcValue - encaissement;
              return value === (expected >= 0 ? expected : 0);
            } else {
              const montantPayeValue = montantPaye.value || 0;
              const expected = montantTtcValue - montantPayeValue;
              return value === (expected >= 0 ? expected : 0);
            }
          }
        ),
      billetageList: Yup.array().when('modePaiement', {
        is: 1,
        then: (schema) =>
          schema.of(
            Yup.object().shape({
              qteBillet: Yup.number()
                .transform((value, originalValue) =>
                  originalValue === '' || originalValue === null ? 0 : value
                )
                .typeError('La quantité doit être un nombre')
                .required('La quantité est obligatoire')
                .integer('La quantité doit être un entier')
                .min(0, 'La quantité ne peut pas être négative')
                .default(0)
            })
          ).required('Les encaissements sont obligatoires pour le paiement en espèces')
            .test(
              'encaissement-min',
              'Le total encaissé doit être supérieur à 0',
              function () {
                const encaissement = montantTotalEncaissement.value || 0;
                return encaissement > 0;
              }
            ),
        otherwise: (schema) => schema.notRequired()
      }),
      billetageSortieList: Yup.array().when('modePaiement', {
        is: 1,
        then: (schema) =>
          schema.of(
            Yup.object().shape({
              qteBillet: Yup.number()
                .transform((value, originalValue) =>
                  originalValue === '' || originalValue === null ? 0 : value
                )
                .typeError('La quantité doit être un nombre')
                .required('La quantité est obligatoire')
                .integer('La quantité doit être un entier')
                .min(0, 'La quantité ne peut pas être négative')
                .default(0)
            })
          ).required('Les reliquats sont obligatoires pour le paiement en espèces')
            .test(
              'reliquat-not-exceed',
              'Le total du billetage de sortie ne peut pas dépasser le reliquat',
              function () {
                const sortie = montantTotalSortie.value || 0;
                const reliquat = reliquatCalcule.value || 0;
                return sortie <= reliquat;
              }
            ),
        otherwise: (schema) => schema.notRequired()
      }),
      reliquatRetourner: Yup.number()
        .typeError('Le reliquat à retourner doit être un nombre valide')
        .min(0, 'Le reliquat à retourner ne peut pas être négatif')
        .test(
          'reliquat-retourner-match',
          'Le reliquat à retourner doit correspondre au calcul',
          function () {
            const reliquat = reliquatCalcule.value || 0;
            const sortie = montantTotalSortie.value || 0;
            const expectedReliquatRetourner = reliquat - sortie >= 0 ? reliquat - sortie : 0;
            return reliquatRetourner.value === expectedReliquatRetourner;
          }
        ),
    });

    const router = useRouter();
    const store = useAuthStore();
    const billetageList = reactive<BilletageForm[]>([]);
    const billetageSortieList = reactive<BilletageForm[]>([]);
    const monnaieList = ref<any[]>([]);
    const montantTotalEncaissement = ref<number | null>(null);
    const montantTotalSortie = ref<number | null>(null);
    const commandesOptions = ref([]);
    const montant = ref<number | null>(null);
    const montantTtc = ref<number | null>(null);
    const typeOperationId = ref<number>(1);
    const billetageForm = ref<InstanceType<typeof Form> | null>(null);
    const totalEntree = ref<number>(0);
    const totalSorties = ref<number>(0);
    const { resetForm } = useForm();
    const selectedCommande = ref(null);
    const modePaiementOptions = ref([]);
    const selectedModePaiement = ref<number | null>(1);
    const banqueOptions = ref([]);
    const montantPaye = ref<number | null>(null);
    const numeroMobileMoney = ref();
    const validPhone = ref<boolean>(false);
    const codepay = ref();
    const nationalnumlber = ref();
    const numberPhone = ref();
    const countriesRef = ref(countries);
    const numeroNational = ref();
    const changementMode = ref<boolean>(false);
    const reliquatRetourner = ref(0);
    const caisseId = ref<number | null>(null);
    const showCaisseClosedMessage = ref<boolean>(false);
    const caisseActive = ref<boolean>(false);
    const selectedTypeReglement = ref<string | null>(null); 
  

    const montantManquant = computed(() => {
      if (!montantTotalEncaissement.value || !montantTtc.value) {
        return 0;
      }
      const difference = montantTtc.value - montantTotalEncaissement.value;
      return difference > 0 ? difference : 0;
    });

    const veriferCaisseOuverte = async () => {
      store.loadCaisseFromStorage();
      console.log("storeee", store.isCaisseOuverte);

      if (store.isCaisseOuverte()) {
        caisseActive.value = true;
        caisseId.value = store.getCaisseId();
        showCaisseClosedMessage.value = false;
        console.log("Caisse déjà en session:", caisseId.value);
        return;
      }

      try {
        const response = await ApiService.get("/ouvfer");
        console.log("Réponse /ouvfer:", response.data);

        if (response.data.code === 200) {
          if (response.data.data) {
            const ouvertureData = response.data.data;
            store.setCaisseOuverte(ouvertureData);
            caisseActive.value = true;
            caisseId.value = ouvertureData.id;
            showCaisseClosedMessage.value = false;

            console.log("Caisse ouverte chargée depuis API:", {
              ouvertureId: ouvertureData.id,
              caisseId: ouvertureData.caisse.id,
              nom: ouvertureData.caisse.nom
            });
          } else {
            caisseActive.value = false;
            caisseId.value = null;
            showCaisseClosedMessage.value = true;

            const confirmed = await monSwal(
              "Aucune caisse ouverte n'est disponible. Voulez-vous ouvrir une caisse maintenant ?",
              "Ouvrir la caisse",
              "btn btn-primary",
              "Plus tard",
              "Vous pourrez ouvrir une caisse plus tard."
            );

            if (confirmed) {
              ouvrirCaisse();
            }

            console.warn("Aucune caisse ouverte trouvée dans l'API");
          }
        }
      } catch (err: any) {
        console.error("Erreur vérification caisse:", err);
        caisseActive.value = false;
        caisseId.value = null;
        showCaisseClosedMessage.value = true;

        const msg = err.response?.data?.message || "Impossible de vérifier la caisse";
        error(msg);
      }
    };

    const ouvrirCaisse = () => {
      router.push({ name: 'AddOuvFermPage' })
    };

    const montantError = computed(() => {
      if (selectedModePaiement.value !== 1) {
        return null;
      }

      if (montantTtc.value === null || montantTotalEncaissement.value === null) {
        return null;
      }

      const encaissement = montantTotalEncaissement.value || 0;
      const ttc = montantTtc.value || 0;
      const sortie = montantTotalSortie.value || 0;
      const reliquat = encaissement - ttc;

      if (reliquat > 0 && sortie > reliquat) {
        return `Le billetage de sortie (${sortie} Fcfa) ne peut pas dépasser le reliquat (${reliquat} Fcfa)`;
      }

      return null;
    });

    const fetchCommande = async () => {
      try {
        const response = await ApiService.get('/commandesNonPaye');
        const commandeDonnees = response.data.data;
        commandesOptions.value = commandeDonnees.map((commande) => {
          const montantDejaPaye = commande.montTTC - commande.montantRest;
          const pourcentagePaye = ((montantDejaPaye / commande.montTTC) * 100).toFixed(0);

          return {
            value: commande.id,
            label: `${commande.refCom} - ${commande.client.nomClient} - Reste: ${commande.montantRest} Fcfa ${montantDejaPaye > 0 ? `(${pourcentagePaye}% payé)` : ''}`,
            montTTC: commande.montTTC,
            montantRest: commande.montantRest,
            montantDejaPaye: montantDejaPaye,
            statutPaiement: commande.statutPaiement
          };
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des commandes:', error);
      }
    };

    const getCurrentDate = () => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };

    const reliquatCalcule = computed(() => {
      if (!montantTotalEncaissement.value || !montantTtc.value) {
        return 0;
      }
      const difference = montantTotalEncaissement.value - montantTtc.value;
      return difference > 0 ? difference : 0;
    });

    const calculateReliquatRetourner = () => {
      const sortie = montantTotalSortie.value || 0;
      const reliquat = reliquatCalcule.value || 0;
      reliquatRetourner.value = reliquat - sortie >= 0 ? reliquat - sortie : 0;
    };

    watch(montantPaye, () => {
    if (selectedTypeReglement.value === 'Commande.') {
      calculateMontantRest(); 
    }
    // 
  });

    const fetchNumeroPaiement = async () => {
      try {
        const response = await ApiService.get("all/modepaiements");
        const modeDonnees = response.data.data.data;
        modePaiementOptions.value = modeDonnees.map((mp) => ({
          value: mp.id,
          label: `${mp.libelle}`,
        }));

        if (modePaiementOptions.value.some(option => option.value === 1)) {
          selectedModePaiement.value = 1;
          if (billetageForm.value) {
            billetageForm.value.setFieldValue('modePaiement', 1);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des modes de paiement:', error);
      }
    };

    const fetchBanque = async () => {
      try {
        const response = await ApiService.get("all/banques");
        const banqueDonnees = response.data.data.data;
        console.log("banquee", banqueDonnees);
        banqueOptions.value = banqueDonnees.map((b) => ({
          value: b.id,
          label: `${b.denominationBanque}`,
        }));
      } catch (error) {
        console.error('Erreur lors de la récupération des banques:', error);
      }
    };

    const calculateMontantRest = () => {
      const montantTtcValue = montantTtc.value || 0;

      if (selectedModePaiement.value === 1) {
        const encaissement = montantTotalEncaissement.value || 0;
        const montantRestValue = montantTtcValue - encaissement;
        if (billetageForm.value) {
          billetageForm.value.setFieldValue('montantRest', montantRestValue >= 0 ? montantRestValue : 0);
        }
      } else {
        const montantPayeValue = montantPaye.value || 0;
        const montantRestValue = montantTtcValue - montantPayeValue;
        if (billetageForm.value) {
          billetageForm.value.setFieldValue('montantRest', montantRestValue >= 0 ? montantRestValue : 0);
        }
      }
    };

    const commandeSelectionnee = computed(() => {
      if (!selectedCommande.value) return null;
      return commandesOptions.value.find(cmd => cmd.value === selectedCommande.value);
    });

    function validate(phoneObject) {
      const isBenin = phoneObject.countryCode === "BJ";
      const nationalNumberClean = phoneObject.nationalNumber?.replace(/\s/g, '') || '';
      const isValidBeninNumber = isBenin && nationalNumberClean.length === 10;

      validPhone.value = phoneObject.valid && isValidBeninNumber;

      if (validPhone.value) {
        numeroMobileMoney.value = phoneObject.number;
        codepay.value = phoneObject.countryCallingCode;
        nationalnumlber.value = phoneObject.nationalNumber;
        numberPhone.value = phoneObject.nationalNumber;
      } else {
        numeroMobileMoney.value = "";
        codepay.value = "";
        nationalnumlber.value = "";
        numberPhone.value = "";
      }
    }

    watch([montantTtc, montantPaye, montantTotalEncaissement, selectedModePaiement], () => {
      calculateMontantRest();
    });

    const handleCommandeChange = (value: any) => {
      selectedCommande.value = value;
      if (!value) {
        montantTtc.value = null;
        montantPaye.value = null;
        return;
      }

      const commande = commandesOptions.value.find(
        (cmd: any) => cmd.value === value
      );

      if (commande) {
        montantTtc.value = commande.montantRest;  

        if (selectedModePaiement.value === 1) {
          montantPaye.value = commande.montantRest;  
          if (billetageForm.value) {
            billetageForm.value.setFieldValue('montantPaye', commande.montantRest);
            billetageForm.value.setFieldValue('montantRest', 0);
          }
        } else {
          montantPaye.value = null;
          if (billetageForm.value) {
            billetageForm.value.setFieldValue('montantRest', commande.montantRest);
          }
        }
      } else {
        montantTtc.value = null;
        montantPaye.value = null;
      }
    };

    const handleModePaiementChange = (value: number) => {
      selectedModePaiement.value = value;
      changementMode.value = value === 1;

      if (value !== 1) {
        montantPaye.value = null;
        billetageList.splice(0, billetageList.length);
        billetageSortieList.splice(0, billetageSortieList.length);
        montantTotalEncaissement.value = null;
        montantTotalSortie.value = null;
        reliquatRetourner.value = 0;

        if (billetageForm.value && montantTtc.value) {
          billetageForm.value.setFieldValue('montantPaye', null);
          billetageForm.value.setFieldValue('montantRest', montantTtc.value);
        }
      } else {
        if (montantTtc.value) {
          montantPaye.value = montantTtc.value;
          if (billetageForm.value) {
            billetageForm.value.setFieldValue('montantPaye', montantTtc.value);
            billetageForm.value.setFieldValue('montantRest', 0);
          }
        }
        getAllMonnaies();
      }
    };

    const getAllMonnaies = async () => {
      try {
        const res = await ApiService.get("/all/monnaies");
        monnaieList.value = res.data.data.data;
        billetageList.splice(0, billetageList.length);
        billetageSortieList.splice(0, billetageSortieList.length);
        monnaieList.value.forEach((element) => {
          const billetage = {
            libelle: element.libelle,
            montant: 0,
            valueAct: element.valeur,
            monnaie: element.id,
            qteBillet: 0,
          };
          billetageList.push({ ...billetage });
          billetageSortieList.push({ ...billetage });
        });
      } catch (err) {
        console.error("Erreur lors de la récupération des monnaies:", err);
        error("Erreur lors de la récupération des monnaies");
      }
    };

    const handleBilletageInput = (event: Event, billetage: BilletageForm, type: 'encaissement' | 'sortie') => {
      const newValue = Number((event.target as HTMLInputElement).value);
      billetage.qteBillet = isNaN(newValue) || newValue < 0 ? 0 : newValue;
      updateMontant(billetage, type);
      calculateMontantRest();
    };

    const updateMontant = (billetage: BilletageForm, type: 'encaissement' | 'sortie') => {
      billetage.montant = billetage.qteBillet * billetage.valueAct || 0;
      if (type === 'encaissement') {
        calculateTotalEncaissement();
      } else {
        calculateTotalSortie();
      }
    };

    const calculateTotalEncaissement = () => {
      const total = billetageList.reduce((total, billetage) => {
        return total + (billetage.montant || 0);
      }, 0);
      montantTotalEncaissement.value = total || null;

      if (billetageForm.value) {
        billetageForm.value.setFieldValue('montantPaye', total);
      }
      montantPaye.value = total;

      calculateReliquatRetourner();
    };

    const calculateTotalSortie = () => {
      const total = billetageSortieList.reduce((total, billetage) => {
        return total + (billetage.montant || 0);
      }, 0);
      montantTotalSortie.value = total || null;
      calculateReliquatRetourner();
    };

    watch([montantTotalEncaissement, montantTotalSortie, montantTtc], () => {
      calculateReliquatRetourner();
    });

    const cancelOperation = async () => {
      const confirmed = await monSwal(
        "Voulez-vous vraiment annuler l'opération ?",
        "Confirmer",
        "btn btn-primary",
        "Annuler",
        "L'opération a été annulée !"
      );
      if (confirmed) {
        if (billetageForm.value) {
          resetForm();
        } else {
          console.warn('billetageForm n\'est pas défini');
        }
        billetageList.length = 0;
        billetageSortieList.length = 0;
        montant.value = null;
        montantTtc.value = null;
        montantPaye.value = null;
        reliquatRetourner.value = 0;
        selectedCommande.value = null;
        selectedModePaiement.value = 1;
        await getAllMonnaies();
        if (billetageForm.value) {
          billetageForm.value.setFieldValue('modePaiement', 1);
        }
        router.push({ name: 'AddPaiement' });
      }
    };


    const addPaiement = async (values: any, { resetForm }: any) => {
      if (montantError.value) {
        error(montantError.value);
        return;
      }

      if (!caisseId.value) {
        Swal.fire({
          icon: "warning",
          title: "Caisse non ouverte",
          text: "Aucune caisse active n'est disponible. Veuillez ouvrir une caisse pour effectuer un paiement.",
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }

      try {
        const encaissement = values.modePaiement === 1 ? montantTotalEncaissement.value : values.montantPaye;
        const ttc = values.montantTtc;
        let statutPaiement = 'En attente';

        if (encaissement >= ttc) {
          statutPaiement = 'Payé';
        } else if (encaissement > 0 && encaissement < ttc) {
          statutPaiement = 'Partiel';
        }

        const payload = {
          caisseId: caisseId.value,
          commande: values.commande,
          montant: values.montantTtc,
          montantTtc: values.montantTtc,
          montantPaye: values.montantPaye,
          montantRest: values.montantRest,
          statutPaiement,
          typeOperationId: typeOperationId.value,
          modePaiement: values.modePaiement,
          datePaiement: values.datePaiement || new Date(),
          type: 'Encaissement',
          billetagesEncaissement: values.modePaiement === 1 ? billetageList : [],
          montantTotalEncaissement: values.modePaiement === 1 ? montantTotalEncaissement.value : null,
          billetagesSortie: values.modePaiement === 1 ? billetageSortieList : [],
          montantTotalSortie: values.modePaiement === 1 ? montantTotalSortie.value || 0 : null,
          reliquat: values.modePaiement === 1 ? reliquatCalcule.value : 0,
          reliquatRetourner: reliquatRetourner.value,
          reliquatNonRendu: values.modePaiement === 1 ? reliquatRetourner.value : 0,
          ...(values.modePaiement === 2 && {
            numeroMobileMoney: values.numeroMobileMoney,
            refOperation: values.refOperation,
          }),
          ...(values.modePaiement === 3 && {
            numCheque: values.numCheque,
            banque: values.banque,
          }),
          ...(values.modePaiement === 4 && {
            numeroVirement: values.numeroVirement,
            banque: values.banque,
          }),
        };

        console.log("Paiement à envoyer:", payload);
        await ApiService.post("/paiements", payload);

        if (statutPaiement === 'Payé') {
          success(`Le paiement a été enregistré avec succès ! La commande est entièrement payée.`);
        } else if (statutPaiement === 'Partiel') {
          success(`Paiement partiel enregistré ! Il reste ${values.montantRest} Fcfa à payer.`);
        }

        resetForm();
        billetageList.length = 0;
        billetageSortieList.length = 0;
        montant.value = null;
        montantTtc.value = null;
        montantPaye.value = null;
        reliquatRetourner.value = 0;
        selectedCommande.value = null;
        selectedModePaiement.value = 1;
        await getAllMonnaies();
        if (billetageForm.value) {
          billetageForm.value.setFieldValue('modePaiement', 1);
        }
        router.push({ name: 'ListePaiement' });
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "Erreur lors de l'ajout de l'opération";
        console.error("Erreur serveur:", errorMessage);
        if (errorMessage === "Impossible de créer un paiement : la caisse n'est pas ouverte.") {
          showCaisseClosedMessage.value = true;
          Swal.fire({
            icon: "warning",
            title: "Caisse non ouverte",
            text: "La caisse n'est pas ouverte. Veuillez ouvrir la caisse avant de faire le paiement.",
            timer: 3000,
            showConfirmButton: false,
          });
        } else {
          error(errorMessage);
        }
      }
    };

    watch(billetageList, () => {
      calculateTotalEncaissement();
    }, { deep: true });

    watch(billetageSortieList, () => {
      calculateTotalSortie();
    }, { deep: true });

    onMounted(async () => {
      await veriferCaisseOuverte();

      if (caisseActive.value) {
        await Promise.all([
          getAllMonnaies(),
          fetchNumeroPaiement(),
          fetchCommande(),
          fetchBanque()
        ]);
      }

      if (billetageForm.value) {
        billetageForm.value.setFieldValue('modePaiement', 1);
        if (montantTtc.value) {
          billetageForm.value.setFieldValue('montantRest', 0);
        }
      }
    });

    const restrictInput = (event: KeyboardEvent) => {
      if (!/^[0-9]*$/.test(event.key) && event.key !== 'Backspace') {
        event.preventDefault();
      }
    };

    return {
      billetageList,
      billetageSortieList,
      montantTotalEncaissement,
      montantTotalSortie,
      paiementSchema,
      restrictInput,
      handleBilletageInput,
      addPaiement,
      commandesOptions,
      montant,
      getCurrentDate,
      banqueOptions,
      montantTtc,
      handleCommandeChange,
      handleModePaiementChange,
      selectedModePaiement,
      modePaiementOptions,
      montantError,
      cancelOperation,
      billetageForm,
      validPhone,
      montantPaye,
      validate,
      numeroMobileMoney,
      reliquatCalcule,
      reliquatRetourner,
      showCaisseClosedMessage,
      ouvrirCaisse,
      caisseActive,
      caisseId,
      montantManquant,
      commandeSelectionnee
    };
  },
});
</script>

<style scoped>
.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.montant-total {
  text-align: left;
  font-weight: bold;
  font-size: 1rem;
  margin-top: 8px;
}

.text-danger {
  font-size: 0.75rem;
  color: #dc3545;
}

.text-warning {
  font-size: 0.75rem;
  color: #ffc107;
}

.compact-input {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  height: 30px;
}

.compact-error {
  font-size: 0.75rem;
  margin-top: 2px;
}

.container-tight {
  margin: 0;
  padding: 2px 0;
}

.is-warning {
  border-color: #ffc107;
}

.disabled-form {
  opacity: 0.6;
  pointer-events: none;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeeba;
  color: #856404;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
}

/* ✅ Styles additionnels pour paiement partiel */
.border-warning {
  border-color: #ff9800 !important;
  border-width: 2px !important;
}

.fa-exclamation-triangle {
  color: #ff9800;
}
</style>